// Mirrors Stevie.Cloud.Api.vNext.Models.OutputFormulaValue (ADR 009 OTV
// Amendment #2). Discriminated union of 6 variants; each variant makes an
// invalid media/family combination structurally impossible. Discriminator
// matches backend `[JsonPolymorphic(TypeDiscriminatorPropertyName = "kind")]`
// (camelCase).

export interface RangeMediaCombo {
  specId: string;
  conventionId: string;
}

export interface OutputFormulaValueRangeStills {
  kind: 'rangeStills';
  stills: RangeMediaCombo;
}

export interface OutputFormulaValueRangeVideos {
  kind: 'rangeVideos';
  videos: RangeMediaCombo;
}

export interface OutputFormulaValueRangeBoth {
  kind: 'rangeBoth';
  stills: RangeMediaCombo;
  videos: RangeMediaCombo;
}

export interface OutputFormulaValueEvStills {
  kind: 'evStills';
  stillsSpecId: string;
  evConventionIds: string[];
}

export interface OutputFormulaValueEvVideos {
  kind: 'evVideos';
  videosSpecId: string;
  evConventionIds: string[];
}

export interface OutputFormulaValueEvBoth {
  kind: 'evBoth';
  stillsSpecId: string;
  videosSpecId: string;
  evConventionIds: string[];
}

export type OutputFormulaValue =
  | OutputFormulaValueRangeStills
  | OutputFormulaValueRangeVideos
  | OutputFormulaValueRangeBoth
  | OutputFormulaValueEvStills
  | OutputFormulaValueEvVideos
  | OutputFormulaValueEvBoth;

export type OutputFormulaValueKind = OutputFormulaValue['kind'];

export const OUTPUT_FORMULA_VALUE_KINDS: readonly OutputFormulaValueKind[] = [
  'rangeStills',
  'rangeVideos',
  'rangeBoth',
  'evStills',
  'evVideos',
  'evBoth',
];

const KIND_LABELS: Record<OutputFormulaValueKind, string> = {
  rangeStills: 'Range · Stills',
  rangeVideos: 'Range · Videos',
  rangeBoth: 'Range · Both',
  evStills: 'EV · Stills',
  evVideos: 'EV · Videos',
  evBoth: 'EV · Both',
};

export const outputFormulaValueKindLabel = (kind: OutputFormulaValueKind): string =>
  KIND_LABELS[kind];

export const outputFormulaValueCoversStills = (value: OutputFormulaValue): boolean => {
  switch (value.kind) {
    case 'rangeStills':
    case 'rangeBoth':
    case 'evStills':
    case 'evBoth':
      return true;
    case 'rangeVideos':
    case 'evVideos':
      return false;
  }
};

export const outputFormulaValueCoversVideos = (value: OutputFormulaValue): boolean => {
  switch (value.kind) {
    case 'rangeVideos':
    case 'rangeBoth':
    case 'evVideos':
    case 'evBoth':
      return true;
    case 'rangeStills':
    case 'evStills':
      return false;
  }
};

export const outputFormulaValueSpecIds = (value: OutputFormulaValue): string[] => {
  switch (value.kind) {
    case 'rangeStills': return [value.stills.specId];
    case 'rangeVideos': return [value.videos.specId];
    case 'rangeBoth': return [value.stills.specId, value.videos.specId];
    case 'evStills': return [value.stillsSpecId];
    case 'evVideos': return [value.videosSpecId];
    case 'evBoth': return [value.stillsSpecId, value.videosSpecId];
  }
};

export const outputFormulaValueIsRangeFamily = (value: OutputFormulaValue): boolean => {
  switch (value.kind) {
    case 'rangeStills':
    case 'rangeVideos':
    case 'rangeBoth':
      return true;
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return false;
  }
};

export const outputFormulaValueIsEvFamily = (value: OutputFormulaValue): boolean =>
  !outputFormulaValueIsRangeFamily(value);
